<template>
  <BittsLoading :is-loading="!popReady || !fileUploadReady">
    <BittsLayout class="populations-page" :is-page="true">
      <template #content>
        <PageTitle title="Populations" :subtitle class="mb-16" />
        <OnboardingCallout
          v-if="!showUniqueRecordInfo"
          kind="populations"
          class="mb-40"
        />
        <UniqueRecordsCallout
          v-if="showUniqueRecordInfo"
          class="mb-40"
          v-model="isRecordExportModalVisible"
        />
        <PopulationsEmptyState v-if="showNoFeedsEmptyState" />
        <div v-else class="flex flex-col gap-40" data-testid="custom-pop-type">
          <StandardTypePopulations
            :populations="standardPopulations"
            :at-or-above-cap="atOrAboveCap"
            @population-clicked="onPopulationClicked"
            @create-population="onCreatePopulation"
            @edit-population="onEditPopulation"
            @view-population="onViewPopulation"
          />
          <CustomTypePopulations
            :populations="customPopsWithStatsAndSharing"
            :disabled="!allowCreatePop"
            :at-or-above-cap="atOrAboveCap"
            @population-clicked="onPopulationClicked"
            @create-population="onCreatePopulation"
            @edit-population="onEditPopulation"
            @view-population="onViewPopulation"
          />
        </div>
      </template>
      <template #populations-modal>
        <router-view-wrapper />
      </template>
    </BittsLayout>
  </BittsLoading>
  <RecordExportSummaryModal
    v-if="isRecordExportModalVisible && showUniqueRecordInfo"
    @closed:record-export-modal="
      isRecordExportModalVisible = !isRecordExportModalVisible
    "
  />
</template>
<script setup lang="ts">
import { BittsLayout, BittsLoading } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import RecordExportSummaryModal from '@/components/integrations/RecordExportSummaryModal.vue';
import OnboardingCallout from '@/components/onboarding/OnboardingCallout.vue';
import PageTitle from '@/components/PageTitle.vue';
import CustomTypePopulations from '@/components/populations/CustomTypePopulations.vue';
import PopulationsEmptyState from '@/components/populations/PopulationsEmptyState.vue';
import StandardTypePopulations from '@/components/populations/StandardTypePopulations.vue';
import UniqueRecordsCallout from '@/components/populations/UniqueRecordsCallout.vue';

import useAuth from '@/composables/useAuth';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import useOnboardingStepStatus from '@/composables/useOnboardingStepStatus';
import usePopulations from '@/composables/usePopulations';
import useRecordExportLimits from '@/composables/useRecordExportLimits';
import {
  COMPLETED_STATUSES,
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  WARNING_STATUSES,
} from '@/constants/data_sources';
import {
  useFeedsStore,
  useFileUploadsStore,
  usePopulationsStore,
} from '@/stores';
import { Population } from '@/types/populations';

const router = useRouter();
const route = useRoute();

const feedsStore = useFeedsStore();
const fileUploadsStore = useFileUploadsStore();
const populationsStore = usePopulationsStore();

const title = computed(() => {
  const population = populationsStore.getPopulationById(
    Number(route.params?.population_id),
  );
  switch (route.name) {
    case 'create_population':
      return 'Create Population - Crossbeam';
    case 'edit_population':
      return population?.name
        ? `Edit ${population.name} - Populations - Crossbeam`
        : 'Edit Population - Crossbeam';
    case 'view_population':
      return population?.name
        ? `${population.name} - Populations - Crossbeam`
        : 'Populations - Crossbeam';
    default:
      return 'Populations - Crossbeam';
  }
});
useHead({ title });

const { isTrialOver } = useConnectorTrialEndDate();

const { feeds } = storeToRefs(feedsStore);
const { uploadTables, ready: fileUploadReady } = storeToRefs(fileUploadsStore);
const { populations, ready: popReady } = storeToRefs(populationsStore);

const { onboardingClosed } = useOnboardingStepStatus();
const { atOrAboveCap, standardPopulations, customPopsWithStatsAndSharing } =
  usePopulations();

const { hasScope } = useAuth();
const canManagePopulations = computed(() => hasScope('write:populations'));
const canReadBilling = computed(() => hasScope('read:billing'));

const hasPops = computed(() => populations.value.length > 0);
const isOnboarding = computed(() => !onboardingClosed.value && !hasPops.value);

const isRecordExportModalVisible = ref(false);

const { exportLimit } = useRecordExportLimits();
const showUniqueRecordInfo = computed(() => {
  if (!populations.value.length) return false;
  return !!exportLimit.value && canReadBilling.value;
});
const subtitle =
  'Populations are segments of accounts that can be shared with partners';

function navigateToPopulationRoute(
  routeName: string,
  population?: Population | null,
) {
  const params: Record<string, number> = {};
  if (
    population?.id &&
    (routeName === 'edit_population' || routeName === 'view_population')
  ) {
    params.population_id = population.id;
  }

  const query: Record<string, string> = {};
  if (population?.standard_type) {
    query.standardType = population.standard_type;
  }

  router.push({
    name: routeName,
    params: Object.keys(params).length > 0 ? params : undefined,
    query: Object.keys(query).length > 0 ? query : undefined,
  });
}

function onPopulationClicked(pop: Population | null) {
  if (pop) {
    const isEmpty = (pop as Population & { isEmpty?: boolean }).isEmpty;
    const routeName =
      isEmpty === true ? 'create_population' : 'edit_population';
    navigateToPopulationRoute(routeName, pop);
  }
}

function onCreatePopulation(pop: Population | null) {
  navigateToPopulationRoute('create_population', pop);
}

function onEditPopulation(pop: Population | null) {
  if (pop) {
    navigateToPopulationRoute('edit_population', pop);
  }
}

function onViewPopulation(pop: Population | null) {
  if (pop) {
    navigateToPopulationRoute('view_population', pop);
  }
}

/* Empty State post-Onboarding Flow */
const showNoFeedsEmptyState = computed(
  () => !hasFeeds.value && !populations.value.length,
);
const hasFeeds = computed(() => {
  const feedsInCompleteOrWarningState = feeds.value.filter((feed) => {
    const isFileUpload = feed.integration.type !== FILE_UPLOAD_DATA_SOURCE_TYPE;
    const isPendingOrActiveOrWarning = [
      ...COMPLETED_STATUSES,
      ...WARNING_STATUSES,
    ].includes(feed.status);
    return isFileUpload && isPendingOrActiveOrWarning;
  });

  return (
    !!feedsInCompleteOrWarningState.length ||
    (!!uploadTables.value && !!uploadTables.value.length)
  );
});

const allowCreatePop = computed(
  () =>
    canManagePopulations.value &&
    !atOrAboveCap.value &&
    !isOnboarding.value &&
    !isTrialOver.value,
);
</script>
